<script>
  import { onMount } from "svelte";
  import { loadStripe } from "@stripe/stripe-js";
  import { Modal, tooltip } from "svelte-utilities";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import NotesIcon from "@local/assets/icons/notes.svg";
  import { Sentry } from "#src/glitchtip.js";
  import { profile } from "#src/stores/auth.js";
  import {
    createStripeCheckoutSession,
    getStripeTransactions,
    getBalances,
    getCustomerId,
    getPrices,
    purchasePackage,
  } from "#src/api/stripe.js";
  import { STRIPE_PUBLIC_KEY } from "#src/env.js";
  import { alerts } from "#src/stores/alerts.js";

  export let org;

  let stripe;
  let customerId;

  let tallyCreditsModal;
  let creditOption = null;
  let transactions = [];
  let priceOptions = [];
  let paymentLast4 = null;
  let hasPaymentOnFile = false;
  let page = 0;
  let hasMore = false;
  let confirmingPayment = false;
  let purchasing = false;
  let purchaseError = null;
  let balances = null;

  $: capabilities = $profile?.profile_capabilities?.capabilities || {};

  async function getPaymentLink() {
    const session = await createStripeCheckoutSession({
      price: creditOption,
      quantity: 1,
      success_url: "settings/account",
      cancel_url: "settings/account",
      mode: "payment",
      customer_id: customerId,
    });
    if (session.url) {
      window.location.href = session.url;
    } else {
      alert("Error creating checkout session. Please try again.");
      Sentry.captureException(new Error("Stripe checkout session creation error", { cause: session }));
      console.log(session);
    }
  }

  async function purchaseCredits() {
    purchasing = true;
    const result = await purchasePackage($org.id, creditOption);
    purchasing = false;
    if (result.success) {
      fetchTransactions();
      fetchBalances();
      alerts.add({ type: "ok", message: "Purchase successful!" });

      tallyCreditsModal.close();
    } else if (result.status === "requires_action") {
      const confirm = await stripe.confirmCardPayment(result.clientSecret);
      if (confirm.error) {
        purchaseError = "Authentication failed";
        Sentry.captureException(new Error("Stripe payment confirmation error", { cause: confirm.error }));
      } else {
        fetchTransactions();
        fetchBalances();
        alerts.add({ type: "ok", message: "Purchase successful!" });
        tallyCreditsModal.close();
      }
    } else {
      Sentry.captureException(new Error("Stripe purchase error", { cause: result }));
      purchaseError = result.message || result.error || "Error processing purchase.";
    }
  }

  async function fetchTransactions() {
    const st = await getStripeTransactions($org.id, page);
    if (st.transactions) {
      transactions = st.transactions.filter((t) => t.status === "succeeded");
      hasMore = st.has_more;
    }
  }

  function nextPage() {
    page++;
    fetchTransactions();
  }

  function prevPage() {
    page--;
    fetchTransactions();
  }

  function getDescription(transaction) {
    if (transaction.product) {
      return transaction.product.name;
    } else if (transaction.invoice) {
      if (transaction.invoice.lines?.data.length > 0) {
        return transaction.invoice.lines.data[0].description || transaction.description || "Invoice Payment";
      }
      return transaction.invoice.description || transaction.description || "Invoice Payment";
    } else if (transaction.line_items.length === 1) {
      return transaction.line_items[0].description;
    } else if (transaction.line_items.length === 0) {
      return transaction.description;
    } else {
      return `${transaction.line_items.length} Items`;
    }
  }

  async function fetchBalances() {
    const b = await getBalances($org.id);
    if (!b.balances) return null;
    balances = b.balances;
  }

  async function fetchPrices() {
    const data = await getPrices($org.id);
    if (data.prices) {
      priceOptions = data.prices;
    }

    if (data.has_payment_on_file) {
      hasPaymentOnFile = true;
      paymentLast4 = data.payment_last_4;
    }
  }

  onMount(async () => {
    loadStripe(STRIPE_PUBLIC_KEY).then((s) => {
      stripe = s;
    });

    const res = await getCustomerId($org.id);
    customerId = res.customer_id;

    if (customerId) {
      fetchTransactions();
      fetchBalances();
      fetchPrices();
    }
  });
</script>

<div class="px-4 pb-4 border-b border-gray-300">
  <h2>Account</h2>
</div>

<div class="py-4 sm:px-4 space-y-8 text-sm">
  {#if balances && balances.balance_status !== "uninitialized"}
    <div class="w-full space-y-2 sm:w-96">
      <h3 class="border-b py-2 border-gray-300">Tally Credits</h3>
      <div>
        {#if balances.balance_status === "pending"}
          <div class="flex justify-between items-center">
            <div class="text-gray-400">Available Credits:</div>
            <div class="italic">Pending</div>
          </div>
        {:else if balances.balance_status === "settled"}
          <div class="flex justify-between items-center">
            <div class="text-gray-400">Available Credits:</div>
            <div class="font-bold text-black text-lg">{balances.balance}</div>
          </div>
          <div class="flex justify-end italic text-gray-400 text-xs">
            <div>{balances.total_usages} / {balances.total_grants} used</div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
  <div class="w-full sm:w-96 space-y-4">
    {#if customerId && capabilities["purchase:tally_credits"] && priceOptions.length}
      <button
        class="btn btn-primary w-full"
        on:click={() => {
          purchaseError = null;
          confirmingPayment = false;
          tallyCreditsModal.open();
        }}>Purchase Tally Credits</button>
    {/if}
  </div>

  <div class="purchase-history space-y-2">
    <h3 class="border-b py-2 border-gray-300">Purchase History</h3>
    {#each transactions as transaction}
      {@const description = getDescription(transaction)}
      <div class="flex gap-2 items-center justify-between overflow-hidden">
        <div class="flex gap-2 items-center grow truncate">
          <div class="text-gray-500">
            {new Date(transaction.created * 1000).toLocaleDateString()}
          </div>
          <div class="grow truncate" use:tooltip={{ text: description }}>
            {description}
          </div>
          {#if transaction.invoice?.hosted_invoice_url}
            <a href={transaction.invoice.hosted_invoice_url} target="_blank">
              <NotesIcon />
            </a>
          {/if}
        </div>
        <div>
          {(transaction.amount / 100).toLocaleString("en-US", {
            style: "currency",
            currency: transaction.currency,
          })}
        </div>
      </div>
    {/each}
    {#if hasMore}
      <div class="flex justify-between">
        <div>
          {#if page > 0}
            <button class="btn-text" on:click={prevPage}>Previous Page</button>
          {/if}
        </div>
        <button class="btn-text" on:click={nextPage}>Next Page</button>
      </div>
    {/if}
  </div>
</div>

<Modal width="28rem" bind:this={tallyCreditsModal} closeable on:confirm={purchaseCredits}>
  <div slot="title">Purchase Additional Tally Credits</div>
  <div slot="content" class="text-sm space-y-2">
    {#if purchasing}
      <div class="flex gap-2 items-center">
        <div class="animate-spin w-4 h-4">
          <SpinnerIcon />
        </div>
        <div>Processing your purchase...</div>
      </div>
    {:else if purchaseError}
      <div class="text-red-500">{purchaseError}</div>
      <div class="flex justify-end gap-2">
        <button
          class="btn"
          on:click={() => {
            tallyCreditsModal.close();
          }}>Close</button>
        <button class="btn btn-primary" on:click={getPaymentLink}> Get Payment Link </button>
      </div>
    {:else if confirmingPayment}
      {@const option = priceOptions.find((o) => o.price_id === creditOption)}
      {@const price_dollars = option.amount / 100}
      {@const credits = option.num_credits}
      {@const price = price_dollars.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      })}
      {@const price_per = (price_dollars / credits).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      })}
      <div>Confirm your purchase of the following bundle:</div>
      <div class="p-4 flex gap-4 items-start">
        <div>
          <div class="font-bold">
            {price}
          </div>
          <div class="text-xs text-gray-400"></div>
        </div>
        <div>
          <div>
            {credits} Job Credit{credits > 1 ? "s" : ""}
          </div>
          <div class="text-xs text-gray-400">
            (valid for {option.product_attrs.metadata.validity_months} mos.)
          </div>
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <button
          class="btn"
          on:click={() => {
            confirmingPayment = false;
            tallyCreditsModal.close();
          }}>
          Cancel
        </button>
        <button class="btn btn-primary" on:click={purchaseCredits}> Confirm </button>
      </div>
    {:else}
      <p>Select a bundle:</p>
      <table>
        {#each priceOptions as option}
          {@const price_dollars = option.amount / 100}
          {@const credits = option.num_credits}
          {@const price = price_dollars.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
          })}
          {@const price_per = (price_dollars / credits).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
          })}
          <tr>
            <td>
              <div class="flex gap-2 items-center">
                <input type="radio" id={option.price_id} value={option.price_id} bind:group={creditOption} />
                <div>{credits} Job Credit{credits > 1 ? "s" : ""}</div>
              </div>
            </td>
            <td>
              <div>
                {price}
                <span class="text-gray-400 text-xs">total</span>
              </div>
              <div class="text-xs text-gray-400">
                ({credits > 1 ? `${price_per} ` : ""}per standard job, valid for {option.product_attrs
                  .metadata.validity_months} mos.)
              </div>
            </td>
          </tr>
        {/each}
      </table>
      <div class="flex justify-end gap-2">
        <button
          class="btn"
          on:click={() => {
            tallyCreditsModal.close();
          }}>Cancel</button>
        <button
          class="btn"
          class:btn-primary={!hasPaymentOnFile}
          on:click={getPaymentLink}
          disabled={!creditOption}>Get Payment Link</button>
        {#if hasPaymentOnFile}
          <button
            class="btn btn-primary"
            on:click={() => {
              confirmingPayment = true;
            }}
            disabled={!creditOption}>
            Use Saved Card ({paymentLast4})
          </button>
        {/if}
      </div>
    {/if}
  </div>
</Modal>

<style>
  td {
    vertical-align: top;
    padding-right: 1rem;
    padding-bottom: 0.5rem;
  }

  .purchase-history {
    width: 100%;
    overflow: hidden;

    @media (min-width: 40rem) {
      max-width: 30rem;
    }
  }
</style>
